import { useState, useContext, FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { ErrorCircleRegular } from '@fluentui/react-icons'

import styles from './Login.module.css'
import Contoso from '../../assets/Contoso.svg'
import { login, verify2FA, selectAgency, AgencyOption } from '../../api'
import { AppStateContext } from '../../state/AppProvider'

const Login = () => {
  const navigate = useNavigate()
  const appStateContext = useContext(AppStateContext)
  const ui = appStateContext?.state.frontendSettings?.ui

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // 2FA state
  const [pendingToken, setPendingToken] = useState<string | null>(null)
  const [maskedEmail, setMaskedEmail] = useState('')
  const [code, setCode] = useState('')
  const [showCode, setShowCode] = useState(false)

  // Agency-selection state (only when the account belongs to >1 agency)
  const [selectToken, setSelectToken] = useState<string | null>(null)
  const [agencies, setAgencies] = useState<AgencyOption[]>([])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const result = await login(email, password)

      if (result.success && result.requires_2fa) {
        setPendingToken(result.pending_token || null)
        setMaskedEmail(result.masked_email || '')
      } else if (result.success && result.user) {
        appStateContext?.dispatch({ type: 'SET_USER', payload: result.user })
        navigate('/')
      } else {
        setError(result.error || 'Login failed. Please try again.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const result = await verify2FA(pendingToken!, code)

      if (result.success && result.requires_agency_selection) {
        setSelectToken(result.select_token || null)
        setAgencies(result.agencies || [])
      } else if (result.success && result.user) {
        appStateContext?.dispatch({ type: 'SET_USER', payload: result.user })
        navigate('/')
      } else {
        setError(result.error || 'Verification failed. Please try again.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSelectAgency = async (agencyid: number) => {
    setError(null)
    setIsLoading(true)

    try {
      const result = await selectAgency(selectToken!, agencyid)
      if (result.success && result.user) {
        appStateContext?.dispatch({ type: 'SET_USER', payload: result.user })
        navigate('/')
      } else {
        setError(result.error || 'Could not open that agency. Please try again.')
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setPendingToken(null)
    setSelectToken(null)
    setAgencies([])
    setCode('')
    setError(null)
  }

  // Agency selection screen (account belongs to more than one agency)
  if (selectToken) {
    return (
      <div className={styles.container}>
        <div className={styles.loginBox}>
          <img
            src={ui?.chat_logo ? ui.chat_logo : Contoso}
            className={styles.logo}
            aria-hidden="true"
            alt="Logo"
          />
          <h1 className={styles.title}>Choose an agency</h1>
          <p className={styles.subtitle}>Select the agency you want to work in</p>

          <div className={styles.form}>
            {error && (
              <div className={styles.error}>
                <ErrorCircleRegular className={styles.errorIcon} />
                <span>{error}</span>
              </div>
            )}

            {agencies.map(a => (
              <button
                key={a.agencyid}
                type="button"
                className={styles.submitButton}
                disabled={isLoading}
                onClick={() => handleSelectAgency(a.agencyid)}
              >
                {a.agency_name}
                {a.role ? ` · ${a.role}` : ''}
              </button>
            ))}

            <button
              type="button"
              className={styles.backButton}
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 2FA code entry screen
  if (pendingToken) {
    return (
      <div className={styles.container}>
        <div className={styles.loginBox}>
          <img
            src={ui?.chat_logo ? ui.chat_logo : Contoso}
            className={styles.logo}
            aria-hidden="true"
            alt="Logo"
          />
          <h1 className={styles.title}>Verify Your Identity</h1>
          <p className={styles.subtitle}>
            A verification code has been sent to {maskedEmail}
          </p>

          <form className={styles.form} onSubmit={handleVerify}>
            {error && (
              <div className={styles.error}>
                <ErrorCircleRegular className={styles.errorIcon} />
                <span>{error}</span>
              </div>
            )}

            <div className={styles.inputGroup}>
              <label htmlFor="code" className={styles.label}>
                Verification Code
              </label>
              <div className={styles.inputWrapper}>
                <input
                  id="code"
                  type={showCode ? 'text' : 'password'}
                  className={styles.input}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  disabled={isLoading}
                  required
                  autoComplete="one-time-code"
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                />
                <button
                  type="button"
                  className={styles.eyeButton}
                  onClick={() => setShowCode(v => !v)}
                  tabIndex={-1}
                >
                  {showCode ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={styles.submitButton}
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </button>

            <button
              type="button"
              className={styles.backButton}
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              Back to login
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.loginBox}>
        <img
          src={ui?.chat_logo ? ui.chat_logo : Contoso}
          className={styles.logo}
          aria-hidden="true"
          alt="Logo"
        />
        <h1 className={styles.title}>{ui?.title || 'Welcome'}</h1>
        <p className={styles.subtitle}>Sign in to continue</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          {error && (
            <div className={styles.error}>
              <ErrorCircleRegular className={styles.errorIcon} />
              <span>{error}</span>
            </div>
          )}

          <div className={styles.inputGroup}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              className={styles.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={isLoading}
              required
              autoComplete="username"
              autoFocus
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <div className={styles.inputWrapper}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={isLoading}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className={styles.eyeButton}
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading || !email || !password}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default Login
